// A compact "attach a screenshot" control: thumbnail strip + a ＋ tile, with the
// shared type/size validation. Used wherever a message about a report can carry
// visual evidence — the reporter's reply and send-back (Product → My reports) and
// the engineer's reply on the board (Admin → Tickets) — so both surfaces behave
// identically. The initial report (ReportForm) has its own richer strip (screen
// recording) but shares the same lib.
//
// Mobile-first: `accept="image/*"` lets a phone offer Camera / Photo library, tap
// targets are ≥40px, and the strip wraps instead of overflowing.
import { useState } from 'react';
import { addPickedFiles, MAX_FILES } from '../lib/attachments.js';
import Icon from './Icon.jsx';

export default function AttachPicker({
  files, onChange, max = MAX_FILES, images = true, label = 'Attach a screenshot', compact = false,
}) {
  const [error, setError] = useState('');
  const size = compact ? 40 : 56;

  // Copy the pick BEFORE clearing the input: `input.files` is a live FileList,
  // so `.value = ''` empties it under any reference already taken (see the same
  // note in ReportForm) and the reply would post with no screenshot attached.
  async function pick(e) {
    const picked = Array.from(e.target.files || []);
    e.target.value = ''; // allow re-picking the same file
    setError('');
    if (!picked.length) return; // picker cancelled
    const r = await addPickedFiles(picked, files, { images, max });
    if (r.error) setError(r.error);
    onChange(r.files);
  }
  const remove = (i) => { setError(''); onChange((files || []).filter((_, j) => j !== i)); };

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        {(files || []).map((f, i) => (
          <div key={i} style={{
            position: 'relative', width: size, height: size, borderRadius: 8, overflow: 'hidden',
            border: '1px solid var(--hairline)', background: 'rgba(128,128,128,0.08)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            {f.isImage ? <img src={f.data} alt={f.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <Icon name="live" size={20} style={{ color: 'var(--muted)' }} />}
            <button type="button" onClick={() => remove(i)} aria-label={`Remove ${f.name}`} style={{
              position: 'absolute', top: 1, right: 1, width: 18, height: 18, borderRadius: '50%',
              border: 'none', background: 'rgba(0,0,0,0.6)', color: '#fff', fontSize: 11, lineHeight: 1, cursor: 'pointer',
            }}>×</button>
          </div>
        ))}
        {(files || []).length < max && (
          <label style={{
            display: 'inline-flex', alignItems: 'center', gap: 6, minHeight: 40, padding: '0 12px',
            borderRadius: 10, border: '1px dashed var(--hairline)', cursor: 'pointer',
            color: 'var(--muted)', fontSize: 13, fontWeight: 600,
          }} title={label}>
            <Icon name="links" size={14} /> <span>{(files || []).length ? 'Add another' : label}</span>
            <input type="file" accept={images ? 'image/*' : 'image/*,video/*'} multiple onChange={pick} style={{ display: 'none' }} />
          </label>
        )}
      </div>
      {error && <p style={{ color: 'var(--brand)', fontSize: 12.5, margin: '6px 0 0' }}>{error}</p>}
    </div>
  );
}

// Read-only strip of attachments that came back from the server (a reply's or a
// report's). Images link out full-size; videos play inline.
export function AttachThumbs({ items, size = 72 }) {
  if (!(items || []).length) return null;
  return (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 6 }}>
      {items.map((a) => (
        a.mime?.startsWith('video/')
          ? <video key={a.id} src={a.url} controls style={{ width: size * 2.2, borderRadius: 8, border: '1px solid var(--hairline)' }} />
          : (
            <a key={a.id} href={a.url} target="_blank" rel="noreferrer" title={`${a.name} — open full size`}>
              <img src={a.url} alt={a.name} style={{ width: size, height: size, objectFit: 'cover', borderRadius: 8, border: '1px solid var(--hairline)' }} />
            </a>
          )
      ))}
    </div>
  );
}
